import { useActiveRoute } from 'app/components/router/ActiveRoute';
import { useRedirect } from 'app/components/router/Redirect';
import IRoute from 'app/constants/Routes/IRoute';
import { isUrlOffAppSite } from 'app/utils/isUrlOffAppSite';
import { getFullUrl } from 'app/utils/routes';
import { useCallbackOne } from 'app/utils/useMemoOne';
import { Record } from 'immutable';
import React, { FunctionComponent } from 'react';
import { parseUrlLoose } from 'app/utils/LooseURL';

export type RouteExcludes = Array<IRoute | IConditionalExclude>;

type UseRedirectArg = Parameters<typeof useRedirect>[0];

export interface IProps extends Omit<UseRedirectArg, 'method' | 'withRedirectTo'> {
  when: boolean;
  // The default behavior is to have a redirectTo query set with the exact location we are about
  // to redirect away from. The main use case for the ConditionalRedirect component is to send
  // the user to an intermediary screen where they will do something and be sent back to where
  // they were. If this behavior is not desired, pass the noRedirectTo flag.
  noRedirectTo?: boolean;
  excludeRoutes?: RouteExcludes;
  method?: 'smartPush' | 'smartReplace',
}

interface IConditionalRedirectProps extends Pick<IProps, 'when' | 'to'> {
  excludeRoutes?: RouteExcludes;
}

// For conditional excludes, if any key in the whenQueryKey array is present in the active route's query,
// or any key value pair in the whenQueryValue matches up with a key value pair in the active route's query
// the route will be excluded from the conditional redirect.
interface IConditionalExclude {
  routes: IRoute[] | null;
  whenQueryKey: string[] | null;
  whenQueryValue: Array<{ [key: string]: string }> | null;
  unlessQueryKey: string[] | null;
  unlessQueryKeyValue: Array<{ [key: string]: string }> | null;
}

export const ConditionalExclude = Record<IConditionalExclude>({
  routes: null,
  whenQueryKey: null,
  whenQueryValue: null,
  unlessQueryKey: null,
  unlessQueryKeyValue: null,
});

const isConditionalExclude = (exclude: IRoute | IConditionalExclude): exclude is IConditionalExclude => {
  try {
    const { whenQueryKey, whenQueryValue, routes } = exclude as IConditionalExclude;
    return Boolean((whenQueryKey || whenQueryValue) && routes);
  } catch {
    return false;
  }
};

function useShouldRedirect({
  to,
  excludeRoutes,
  when,
}: IConditionalRedirectProps) {
  const activeRoute = useActiveRoute();
  const isOffApp = isUrlOffAppSite(typeof to === 'string' ? to : getFullUrl(to));
  const excludeConditionMet = useCallbackOne((conditionalExclude: IConditionalExclude) => {
    const { query: activeQuery, route: currentRoute } = activeRoute;
    const {
      routes,
      whenQueryKey,
      whenQueryValue,
      unlessQueryKey,
      unlessQueryKeyValue,
    } = conditionalExclude;

    if (routes && currentRoute && routes.every((toExclude) => toExclude !== currentRoute)) {
      return false;
    }

    // Don't exclude the routes if any key in unlessQueryKey is present in the query
    if (unlessQueryKey && unlessQueryKey.some((key) => key in activeQuery)) {
      return false;
    }

    const checkKeyValuePair = (queryKeyValue: { [key: string]: string }) => Object.keys(queryKeyValue).every(
      (queryKey) => activeQuery[queryKey] === queryKeyValue[queryKey],
    );

    // Don't exclude the routes if any key-value pair in unlessQueryKeyValue is present in the query
    if (unlessQueryKeyValue && unlessQueryKeyValue.some((kv) => checkKeyValuePair(kv))) {
      return false;
    }

    let keyPresenceMet = false;
    let keyValuePresenceMet = false;

    if (whenQueryKey) {
      keyPresenceMet = whenQueryKey.some((key) => key in activeQuery);
    }

    // This can be used to either check for 'or' or 'and' key value matching depending on the passed in array.
    // whenQueryValue of [{ a: 'yes', b: 'yes'}], condition met if activeQuery has both 'a' and 'b' with value of 'yes'
    // whenQueryValue of [{ a: 'yes'}, { b: 'yes'}], condition met if activeQuery has either 'a' or 'b' with value 'yes'
    if (whenQueryValue) {
      keyValuePresenceMet = whenQueryValue.some((queryKeyValue) => checkKeyValuePair(queryKeyValue));
    }

    return keyPresenceMet || keyValuePresenceMet;
  }, [activeRoute]);

  // Decide if we should redirect based on props and the history + excluded routes
  const shouldRedirect = useCallbackOne(() => {
    const { pathname } = typeof to === 'string' ? parseUrlLoose(to) : to;
    if (activeRoute.route === undefined || (pathname && activeRoute.route.match(pathname) && !isOffApp)) {
      return false;
    }

    const excludes = excludeRoutes && excludeRoutes.reduce((acc: IRoute[], currentExclude) => {
      if (isConditionalExclude(currentExclude)) {
        if (currentExclude.routes && excludeConditionMet(currentExclude)) {
          return acc.concat(currentExclude.routes);
        }
      } else {
        acc.push(currentExclude);
      }
      return acc;
    }, []) || [];

    const onExcludedPage = excludes.some(route => route === activeRoute.route);

    return when && !onExcludedPage;
  }, [activeRoute.route, excludeConditionMet, excludeRoutes, isOffApp, to, when]);

  return shouldRedirect;
}

const ConditionalRedirect: FunctionComponent<IProps> = ({
  children,
  noRedirectTo = false,
  ...params
}) => {
  const shouldRedirect = useShouldRedirect(params)();

  useRedirect({
    ...params,
    withRedirectTo: !noRedirectTo,
    shouldRedirect,
  });

  if (shouldRedirect) {
    // Don't render anything, because we should be redirecting to another page anyway.
    return null;
  } else {
    return <>{children}</>;
  }
};

export default ConditionalRedirect;
